if (!customElements.get('product-reviews')) {
  customElements.define(
    'product-reviews',
    class ProductReviews extends HTMLElement {
      constructor() {
        super();
        this.perPage = parseInt(this.dataset.perPage, 10) || 5;
        this.sort = this.dataset.defaultSort || 'newest';
        this.endpoint = this.dataset.endpoint || '';
        this.handle = this.dataset.handle || '';
        this.productId = this.dataset.productId || '';
        this.turnstileKey = (this.dataset.turnstileKey || '').trim();
        this.page = 1;

        this.summaryEl = this.querySelector('[data-pr-summary]');
        this.listEl = this.querySelector('[data-pr-list]');
        this.emptyEl = this.querySelector('[data-pr-empty]');
        this.paginationEl = this.querySelector('[data-pr-pagination]');
        this.sortEl = this.querySelector('[data-pr-sort]');

        this.data = this.parseData();
        this.reviews = Array.isArray(this.data.reviews) ? this.data.reviews.slice() : [];
      }

      connectedCallback() {
        this.renderSummary();
        this.applySort();
        this.render();

        if (this.sortEl) {
          this.sortEl.value = this.sort;
          this.sortEl.addEventListener('change', () => {
            this.sort = this.sortEl.value;
            this.page = 1;
            this.applySort();
            this.render();
          });
        }

        this.setupModal();
      }

      parseData() {
        const script = this.querySelector('script[data-product-reviews]');
        if (!script) return {};
        let raw = (script.textContent || '').trim();
        if (!raw) return {};
        try {
          let parsed = JSON.parse(raw);
          // Metafield kan als string zijn opgeslagen -> dubbel parsen.
          if (typeof parsed === 'string') parsed = JSON.parse(parsed);
          return parsed && typeof parsed === 'object' ? parsed : {};
        } catch (e) {
          console.error('[product-reviews] Kon reviews JSON niet parsen', e);
          return {};
        }
      }

      /* ----------------------------- Sterren ----------------------------- */
      starsMarkup(rating) {
        const value = Math.max(0, Math.min(5, Number(rating) || 0));
        let html = '<span class="product-reviews__stars" aria-hidden="true">';
        for (let i = 1; i <= 5; i++) {
          let fill = 'empty';
          if (value >= i) fill = 'full';
          else if (value >= i - 0.5) fill = 'half';
          html += `<span class="product-reviews__star product-reviews__star--${fill}"></span>`;
        }
        html += '</span>';
        return html;
      }

      /* ----------------------------- Sorteren ---------------------------- */
      applySort() {
        const parseDate = (r) => {
          const t = Date.parse(r && r.date);
          return isNaN(t) ? 0 : t;
        };
        this.reviews.sort((a, b) => {
          if (this.sort === 'highest') return (b.rating || 0) - (a.rating || 0);
          if (this.sort === 'lowest') return (a.rating || 0) - (b.rating || 0);
          return parseDate(b) - parseDate(a); // newest
        });
      }

      /* ----------------------------- Header ------------------------------ */
      renderSummary() {
        if (!this.summaryEl) return;
        const count = typeof this.data.count === 'number' ? this.data.count : this.reviews.length;
        let average = this.data.average;
        if (typeof average !== 'number') {
          average = this.reviews.length
            ? this.reviews.reduce((s, r) => s + (Number(r.rating) || 0), 0) / this.reviews.length
            : 0;
        }
        if (count === 0) {
          this.summaryEl.innerHTML = '';
          return;
        }
        const avgText = average.toFixed(1).replace('.', ',');
        const label = count === 1 ? '1 review' : `${count} reviews`;
        this.summaryEl.innerHTML =
          this.starsMarkup(average) +
          `<span class="product-reviews__avg">${avgText}/5</span>` +
          `<span class="product-reviews__count">(${label})</span>`;
      }

      /* ----------------------------- Lijst ------------------------------- */
      render() {
        if (!this.listEl) return;

        if (!this.reviews.length) {
          this.listEl.innerHTML = '';
          if (this.emptyEl) this.emptyEl.hidden = false;
          if (this.paginationEl) this.paginationEl.hidden = true;
          if (this.sortEl) this.sortEl.parentElement.hidden = true;
          return;
        }
        if (this.emptyEl) this.emptyEl.hidden = true;

        const totalPages = Math.max(1, Math.ceil(this.reviews.length / this.perPage));
        if (this.page > totalPages) this.page = totalPages;
        const start = (this.page - 1) * this.perPage;
        const pageItems = this.reviews.slice(start, start + this.perPage);

        this.listEl.innerHTML = pageItems.map((r) => this.reviewMarkup(r)).join('');
        this.renderPagination(totalPages);
      }

      reviewMarkup(r) {
        const author = this.escape(r.author || 'Anoniem');
        const body = this.escape(r.body || '');
        const date = this.formatDate(r.date);
        const source = r.bron ? this.sourceBadge(r.bron) : '';
        return (
          '<li class="product-reviews__item">' +
          '<div class="product-reviews__item-head">' +
          this.starsMarkup(r.rating) +
          `<span class="product-reviews__author">${author}</span>` +
          source +
          '</div>' +
          (date ? `<div class="product-reviews__date">${date}</div>` : '') +
          `<p class="product-reviews__body">${body}</p>` +
          '</li>'
        );
      }

      sourceBadge(bron) {
        const key = String(bron).toLowerCase();
        const labels = {
          site: 'Afdeling Riemen',
          bol: 'Bol',
          wwk: 'Webwinkel Keur',
          webwinkelkeur: 'Webwinkel Keur',
          kvar: 'KVAR',
        };
        const label = labels[key] || this.escape(bron);
        return `<span class="product-reviews__badge product-reviews__badge--${this.escape(key)}">${label}</span>`;
      }

      formatDate(value) {
        const t = Date.parse(value);
        if (isNaN(t)) return '';
        try {
          return new Date(t).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
        } catch (e) {
          return '';
        }
      }

      /* --------------------------- Paginering ---------------------------- */
      renderPagination(totalPages) {
        if (!this.paginationEl) return;
        if (totalPages <= 1) {
          this.paginationEl.hidden = true;
          this.paginationEl.innerHTML = '';
          return;
        }
        this.paginationEl.hidden = false;
        let html = '';
        html += `<button type="button" class="product-reviews__page-btn" data-pr-prev ${
          this.page === 1 ? 'disabled' : ''
        } aria-label="Vorige">&larr;</button>`;
        for (let p = 1; p <= totalPages; p++) {
          html += `<button type="button" class="product-reviews__page-btn${
            p === this.page ? ' is-active' : ''
          }" data-pr-page="${p}" ${p === this.page ? 'aria-current="page"' : ''}>${p}</button>`;
        }
        html += `<button type="button" class="product-reviews__page-btn" data-pr-next ${
          this.page === totalPages ? 'disabled' : ''
        } aria-label="Volgende">&rarr;</button>`;
        this.paginationEl.innerHTML = html;

        this.paginationEl.querySelectorAll('[data-pr-page]').forEach((btn) => {
          btn.addEventListener('click', () => {
            this.page = parseInt(btn.dataset.prPage, 10);
            this.render();
            this.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          });
        });
        const prev = this.paginationEl.querySelector('[data-pr-prev]');
        const next = this.paginationEl.querySelector('[data-pr-next]');
        if (prev) prev.addEventListener('click', () => this.goTo(this.page - 1, totalPages));
        if (next) next.addEventListener('click', () => this.goTo(this.page + 1, totalPages));
      }

      goTo(page, totalPages) {
        this.page = Math.max(1, Math.min(totalPages, page));
        this.render();
        this.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      /* ----------------------------- Popup ------------------------------- */
      setupModal() {
        this.modal = this.querySelector('[data-pr-modal]');
        if (!this.modal) return;
        this.form = this.modal.querySelector('[data-pr-form]');
        this.messageEl = this.modal.querySelector('[data-pr-message]');
        this.ratingInput = this.modal.querySelector('[data-pr-rating-input]');
        this.ratingField = this.form ? this.form.querySelector('input[name="rating"]') : null;
        this.turnstileContainer = this.modal.querySelector('[data-pr-turnstile]');
        this.turnstileStatus = this.modal.querySelector('[data-pr-turnstile-status]');
        this.turnstileToken = '';
        this.turnstileWidgetId = null;

        this.querySelectorAll('[data-pr-open]').forEach((btn) =>
          btn.addEventListener('click', () => this.openModal())
        );
        this.modal.querySelectorAll('[data-pr-close]').forEach((btn) =>
          btn.addEventListener('click', () => this.closeModal())
        );
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && !this.modal.hidden) this.closeModal();
        });

        this.buildRatingInput();

        if (this.form) this.form.addEventListener('submit', (e) => this.handleSubmit(e));

        // Verplaats de popup naar de body zodat position:fixed t.o.v. het
        // viewport werkt (ge-transformde ouders breken anders de backdrop).
        document.body.appendChild(this.modal);
      }

      buildRatingInput() {
        if (!this.ratingInput) return;
        let html = '';
        for (let i = 1; i <= 5; i++) {
          html += `<button type="button" class="product-reviews__rating-star" data-value="${i}" role="radio" aria-checked="false" aria-label="${i} ${
            i === 1 ? 'ster' : 'sterren'
          }"></button>`;
        }
        this.ratingInput.innerHTML = html;
        this.ratingInput.querySelectorAll('.product-reviews__rating-star').forEach((star) => {
          star.addEventListener('click', () => this.setRating(parseInt(star.dataset.value, 10)));
          star.addEventListener('mouseenter', () => this.highlightRating(parseInt(star.dataset.value, 10)));
        });
        this.ratingInput.addEventListener('mouseleave', () =>
          this.highlightRating(this.ratingField ? parseInt(this.ratingField.value, 10) || 0 : 0)
        );
      }

      setRating(value) {
        if (this.ratingField) this.ratingField.value = value;
        this.highlightRating(value);
        this.ratingInput.querySelectorAll('.product-reviews__rating-star').forEach((star) => {
          star.setAttribute('aria-checked', String(parseInt(star.dataset.value, 10) === value));
        });
      }

      highlightRating(value) {
        this.ratingInput.querySelectorAll('.product-reviews__rating-star').forEach((star) => {
          star.classList.toggle('is-active', parseInt(star.dataset.value, 10) <= value);
        });
      }

      openModal() {
        this.modal.hidden = false;
        document.body.classList.add('overflow-hidden');
        const firstInput = this.form && this.form.querySelector('input[name="author"]');
        if (firstInput) firstInput.focus();
        window.setTimeout(() => this.loadTurnstile(), 50);
      }

      closeModal() {
        this.modal.hidden = true;
        document.body.classList.remove('overflow-hidden');
      }

      /* --------------------------- Turnstile ----------------------------- */
      loadTurnstile() {
        if (!this.turnstileKey) return;
        const container = this.turnstileContainer;
        if (!container) return;

        this.setTurnstileStatus('Verificatie wordt geladen…');
        this.ensureTurnstileScript()
          .then(() => this.renderTurnstile(container))
          .catch((err) => {
            console.error('[product-reviews] Turnstile laden mislukt', err);
            this.showTurnstileFallback(container);
          });
      }

      ensureTurnstileScript() {
        if (window.turnstile && typeof window.turnstile.render === 'function') {
          return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
          let settled = false;
          const finish = (err) => {
            if (settled) return;
            settled = true;
            if (err) reject(err);
            else resolve();
          };

          const interval = setInterval(() => {
            if (window.turnstile && typeof window.turnstile.render === 'function') {
              clearInterval(interval);
              finish();
            }
          }, 100);

          setTimeout(() => {
            clearInterval(interval);
            if (!window.turnstile) {
              finish(new Error('Turnstile timeout'));
            }
          }, 12000);
        });
      }

      renderTurnstile(container) {
        if (!window.turnstile || typeof window.turnstile.render !== 'function') {
          this.showTurnstileFallback(container);
          return;
        }

        if (this.turnstileWidgetId != null) {
          try {
            window.turnstile.reset(this.turnstileWidgetId);
            this.setTurnstileStatus('');
          } catch (e) {
            this.turnstileWidgetId = null;
            container.dataset.rendered = '';
          }
          if (this.turnstileWidgetId != null) return;
        }

        if (container.dataset.rendered === 'true') return;

        this.turnstileToken = '';
        const statusHtml = container.querySelector('[data-pr-turnstile-status]')
          ? container.querySelector('[data-pr-turnstile-status]').outerHTML
          : '';
        container.innerHTML = statusHtml;

        const mount = document.createElement('div');
        mount.setAttribute('data-pr-turnstile-mount', '');
        mount.appendChild(document.createTextNode('\u00a0'));
        container.appendChild(mount);

        const options = {
          sitekey: this.turnstileKey,
          theme: 'light',
          callback: (token) => {
            this.turnstileToken = token || '';
            this.setTurnstileStatus('');
          },
          'expired-callback': () => {
            this.turnstileToken = '';
            try {
              window.turnstile.reset(this.turnstileWidgetId);
            } catch (e) {
              /* ignore */
            }
          },
        };

        try {
          this.turnstileWidgetId = window.turnstile.render(mount, options);
          container.dataset.rendered = 'true';
          this.setTurnstileStatus('');
        } catch (e) {
          console.error('[product-reviews] Turnstile kon niet gerenderd worden', e);
          this.showTurnstileFallback(container);
        }
      }

      setTurnstileStatus(text) {
        if (!this.turnstileContainer) return;
        let status = this.turnstileContainer.querySelector('[data-pr-turnstile-status]');
        if (!text) {
          if (status) status.remove();
          return;
        }
        if (!status) {
          status = document.createElement('p');
          status.className = 'product-reviews__turnstile-status';
          status.setAttribute('data-pr-turnstile-status', '');
          this.turnstileContainer.prepend(status);
        }
        status.textContent = text;
      }

      showTurnstileFallback(container) {
        if (!container) return;
        container.dataset.rendered = '';
        this.turnstileWidgetId = null;
        container.innerHTML =
          '<p class="product-reviews__turnstile-fallback">De robotcheck kon niet geladen worden. Zet eventuele adblockers uit, ververs de pagina en probeer opnieuw.</p>';
      }

      getTurnstileToken() {
        if (!this.turnstileKey) return '';
        if (this.turnstileToken) return this.turnstileToken;
        if (window.turnstile && this.turnstileWidgetId != null) {
          try {
            return window.turnstile.getResponse(this.turnstileWidgetId) || '';
          } catch (e) {
            /* fallback hieronder */
          }
        }
        const field = this.modal ? this.modal.querySelector('[name="cf-turnstile-response"]') : null;
        return field ? field.value : '';
      }

      /* ---------------------------- Submit ------------------------------- */
      async handleSubmit(event) {
        event.preventDefault();
        if (!this.form) return;

        // Honeypot: ingevuld = bot -> stil doen alsof het lukt.
        const honeypot = this.form.querySelector('[name="website"]');
        if (honeypot && honeypot.value.trim() !== '') {
          this.showMessage('Bedankt voor je review!', 'success');
          return;
        }

        const author = this.form.querySelector('[name="author"]').value.trim();
        const email = this.form.querySelector('[name="email"]').value.trim();
        const body = this.form.querySelector('[name="body"]').value.trim();
        const rating = this.ratingField ? parseFloat(this.ratingField.value) : 0;
        const consent = this.form.querySelector('[name="consent"]').checked;

        if (!author || !email || !body || !rating) {
          this.showMessage('Vul alle verplichte velden in (naam, e-mail, score en beoordeling).', 'error');
          return;
        }
        if (author.length < 2) {
          this.showMessage('Vul je naam in (minimaal 2 tekens).', 'error');
          return;
        }
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
          this.showMessage('Vul een geldig e-mailadres in.', 'error');
          return;
        }
        if (body.length < 10) {
          this.showMessage('Je beoordeling is te kort. Schrijf minimaal 10 tekens.', 'error');
          return;
        }
        if (!consent) {
          this.showMessage('Ga akkoord met plaatsing van je review.', 'error');
          return;
        }

        const token = this.getTurnstileToken();
        if (this.turnstileKey && !token) {
          this.showMessage('Bevestig even dat je geen robot bent. Het vakje staat onder de akkoord-tekst.', 'error');
          this.loadTurnstile();
          return;
        }

        const payload = {
          author,
          email,
          rating,
          body,
          consent,
          handle: this.handle,
          shopify_product_id: this.productId,
          website: honeypot ? honeypot.value : '',
          'cf-turnstile-response': token,
        };

        const submitBtn = this.form.querySelector('[data-pr-submit]');
        if (submitBtn) submitBtn.disabled = true;
        this.showMessage('Bezig met verzenden...', 'pending');

        try {
          const response = await fetch(this.endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload),
          });

          if (!response.ok) {
            let msg = 'Er ging iets mis bij het verzenden. Probeer het later opnieuw.';
            try {
              const data = await response.json();
              let raw = data && (data.error || data.message);
              if (!raw && data && data.errors) {
                const first = Object.values(data.errors)[0];
                raw = Array.isArray(first) ? first[0] : first;
              }
              if (raw) msg = this.translateServerError(raw);
            } catch (e) {
              /* geen JSON body */
            }
            throw new Error(msg);
          }

          this.form.reset();
          this.setRating(0);
          this.turnstileToken = '';
          if (window.turnstile && this.turnstileWidgetId != null) window.turnstile.reset(this.turnstileWidgetId);
          this.showMessage('Bedankt! Je review is ontvangen en wordt na controle geplaatst.', 'success');
        } catch (err) {
          this.showMessage(err.message || 'Er ging iets mis. Probeer het later opnieuw.', 'error');
        } finally {
          if (submitBtn) submitBtn.disabled = false;
        }
      }

      translateServerError(raw) {
        const key = String(raw).trim();
        const map = {
          'validation.required': 'Vul alle verplichte velden in.',
          'validation.email': 'Vul een geldig e-mailadres in.',
          'validation.min.string': 'Een van je velden is te kort. Schrijf een iets langere beoordeling (minimaal 10 tekens).',
          'validation.max.string': 'Een van je velden is te lang.',
          'validation.accepted': 'Ga akkoord met plaatsing van je review.',
        };
        if (map[key]) return map[key];
        if (key.indexOf('validation.') === 0) return 'Controleer je invoer en probeer het opnieuw.';
        return key;
      }

      showMessage(text, type) {
        if (!this.messageEl) return;
        this.messageEl.hidden = false;
        this.messageEl.textContent = text;
        this.messageEl.className = `product-reviews__message product-reviews__message--${type}`;
      }

      /* ---------------------------- Helpers ------------------------------ */
      escape(str) {
        const div = document.createElement('div');
        div.textContent = String(str == null ? '' : str);
        return div.innerHTML;
      }
    }
  );
}
